import { isTrackEventLoggingCookieSet } from "@/react/utils/isCookieSet";

import {
  enqueueEvent,
  flushEvents,
  type EventData,
} from "./analyticsEventBatch";
import { sendToEndpoint } from "./analyticsSend";

const EVENT_NAMES = {
  pageView: "PageView",
  pageExit: "PageExit",
  videoView: "VideoView",
  videoSegmentView: "VideoSegmentView",
  postView: "PostView",
  newsletterPostView: "NewsletterPostView",
};

let url: string | undefined;
let token: string | undefined;

const PLATFORM = "web";
const APP_NAME = `circle.${window.RAILS_ENV}`;

type PropertyValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Record<string, unknown>;
export type PropertiesType = { community_id?: number } & Record<
  string,
  PropertyValue
>;

interface TrackEventOptions {
  batch?: boolean;
}

interface IframeDetection {
  isInIframe: boolean;
}

const detectIframe = (): IframeDetection => {
  try {
    return {
      isInIframe: window.self !== window.top,
    };
  } catch {
    // Expected: Cross-origin iframe access throws security exception
    return {
      isInIframe: true,
    };
  }
};

// Cache iframe detection result at module level since it won't change during page lifecycle
const iframeDetection = detectIframe();

const trackEvent = (
  eventName: string,
  properties: PropertiesType,
  options?: TrackEventOptions,
) => {
  const { isInIframe } = iframeDetection;

  if (!url) {
    url = window && window.ANALYTICS_TRACKER_URL;
  }

  if (!token) {
    token = window && window.ANALYTICS_TRACKER_API_TOKEN;
  }

  const tenantId = (properties.community_id ?? 0).toString();

  const defaultProperties = {
    platform: PLATFORM,
    ahoy_visitor: window.ANALYTICS_TRACKER_VISITOR,
    is_in_iframe: isInIframe,
  };

  const data: EventData = {
    event_name: eventName,
    tenant_id: tenantId,
    app_name: APP_NAME,
    triggered_at: new Date().toISOString(),
    properties: { ...defaultProperties, ...properties },
  };

  if (
    isTrackEventLoggingCookieSet() ||
    window.ANALYTICS_TRACKER_LOG_IN_CONSOLE === "true"
  ) {
    // eslint-disable-next-line no-console -- Log info to console. Needed for testing
    console.info("useAnalyticsTracker.trackEvent", data);
  }

  if (url && token) {
    if (options?.batch) {
      enqueueEvent(data);
      return;
    }

    let body;
    try {
      body = JSON.stringify(data);
    } catch (error) {
      console.error("Error while converting data to JSON", error);
      return;
    }

    sendToEndpoint(url, token, body);
  }
};

const trackPageView = (properties: PropertiesType) =>
  trackEvent(EVENT_NAMES.pageView, properties);

const trackPageExit = (properties: PropertiesType) =>
  trackEvent(EVENT_NAMES.pageExit, properties);

const trackVideoView = (properties: PropertiesType) =>
  trackEvent(EVENT_NAMES.videoView, properties);

const trackVideoSegmentView = (properties: PropertiesType) =>
  trackEvent(EVENT_NAMES.videoSegmentView, properties);

const trackPostView = (properties: PropertiesType) =>
  trackEvent(EVENT_NAMES.postView, properties);

const trackNewsletterPostView = (properties: PropertiesType) =>
  trackEvent(EVENT_NAMES.newsletterPostView, properties);

const trackEventsForRevops = (eventName: string, properties: PropertiesType) =>
  trackEvent(eventName, properties);

export const analyticsTrackerApi = {
  trackPageView,
  trackPageExit,
  trackVideoView,
  trackVideoSegmentView,
  trackPostView,
  trackNewsletterPostView,
  trackEvent,
  trackEventsForRevops,
  flushEvents,
};
